/**
 * Incident Context Builder
 * 
 * Builds a formatted context string for the AI assistant from incident data
 * and investigation results.
 */

import type {
  EnrichedIncident,
  Alert,
  Entity,
  MitreMapping,
  TimelineEvent,
  UserInvestigationResult,
  DeviceInvestigationResult,
  InvestigationSignIn,
} from '../data/types';

// ============================================================================
// TYPES
// ============================================================================

export interface IncidentData {
  incident: EnrichedIncident;
  alerts: Alert[];
  entities: Entity[];
  timeline: TimelineEvent[];
  mitre: MitreMapping[];
}

export interface InvestigationCache {
  users: Map<string, UserInvestigationResult>;
  devices: Map<string, DeviceInvestigationResult>;
}

// ============================================================================
// MAIN BUILDER
// ============================================================================

/**
 * Build the full context string for the AI assistant.
 * Includes incident data, AI analysis, alerts, entities, and investigation results.
 */
export function buildFullContext(
  incidentData: IncidentData,
  investigationCache: InvestigationCache
): string {
  const { incident, alerts, entities, mitre, timeline } = incidentData;
  const sections: string[] = [];

  // ===== INCIDENT BASICS =====
  sections.push(`## Incident: ${incident.incidentId}
- **Tenant:** ${incident.tenantDisplayName ?? incident.tenantId}
- **Title:** ${incident.title}
- **Severity:** ${incident.severity.toUpperCase()}
- **Platform Status:** ${incident.platformStatus}
- **Created:** ${formatDateTime(incident.createdTime)}
- **Defender Status:** ${incident.defenderStatus ?? 'N/A'}
- **Assigned To:** ${incident.assignedToName ?? 'Unassigned'}`);

  // ===== AI ANALYSIS =====
  if (incident.summary || incident.reasoning || incident.riskScore != null) {
    let aiSection = '## AI Analysis';
    
    if (incident.summary) {
      aiSection += `\n\n**Summary:**\n${incident.summary}`;
    }
    
    if (incident.reasoning) {
      aiSection += `\n\n**Reasoning:**\n${incident.reasoning}`;
    }
    
    if (incident.riskScore != null) {
      aiSection += `\n\n**Risk Score:** ${incident.riskScore}/10`;
    }
    
    if (incident.aiClassification) {
      aiSection += `\n**Classification:** ${incident.aiClassification}`;
    }
    
    if (incident.aiImmediateAction) {
      aiSection += `\n\n**Recommended Action:**\n${incident.aiImmediateAction}`;
    }
    
    sections.push(aiSection);
  }

  // ===== MITRE ATT&CK =====
  if (mitre.length > 0) {
    const mitreLines = mitre.map(m => {
      const techId = m.techniqueId ? ` (${m.techniqueId})` : '';
      return `- **${m.tactic}:** ${m.technique}${techId}`;
    }).join('\n');
    sections.push(`## MITRE ATT&CK Techniques\n${mitreLines}`);
  }

  // ===== TIMELINE =====
  if (timeline.length > 0) {
    const timelineLines = timeline.slice(0, 10).map(e => {
      const confidence = e.confidence ? ` [${e.confidence} confidence]` : '';
      return `- **${formatDateTime(e.timestamp)}** - ${e.title}${confidence}\n  ${e.description}`;
    }).join('\n');
    sections.push(`## Timeline (${timeline.length} events)\n${timelineLines}`);
  }

  // ===== ALERTS =====
  if (alerts.length > 0) {
    const alertLines = alerts.slice(0, 15).map(a => {
      const source = a.serviceSource ? ` (${a.serviceSource})` : '';
      const threat = a.threatDisplayName ? ` - Threat: ${a.threatDisplayName}` : '';
      return `- [${a.severity.toUpperCase()}] ${a.title}${source}${threat}`;
    }).join('\n');
    sections.push(`## Alerts (${alerts.length} total)\n${alertLines}`);
  }

  // ===== ENTITIES =====
  if (entities.length > 0) {
    const groupedEntities = groupEntitiesByType(entities);
    let entitySection = `## Entities (${entities.length} total)`;
    
    for (const [type, items] of Object.entries(groupedEntities)) {
      const lines = items.slice(0, 8).map(e => {
        const value = e.value && e.value !== e.name ? ` (${e.value})` : '';
        return `  - ${e.name}${value}`;
      }).join('\n');
      entitySection += `\n\n**${capitalizeFirst(type)}s (${items.length}):**\n${lines}`;
    }
    
    sections.push(entitySection);
  }

  // ===== INVESTIGATED USERS =====
  if (investigationCache.users.size > 0) {
    sections.push(buildUserInvestigationsSection(investigationCache.users));
  }

  // ===== INVESTIGATED DEVICES =====
  if (investigationCache.devices.size > 0) {
    sections.push(buildDeviceInvestigationsSection(investigationCache.devices));
  }

  return sections.join('\n\n---\n\n');
}

// ============================================================================
// SECTION BUILDERS
// ============================================================================

function buildUserInvestigationsSection(
  users: Map<string, UserInvestigationResult>
): string {
  let section = `## Investigated Users (${users.size})`;

  for (const [, result] of users) {
    const { user, risk, signIns, devices } = result;

    section += `\n\n### ${user.displayName}
- **UPN:** ${user.userPrincipalName}
- **Account Status:** ${user.accountEnabled ? '✓ Active' : '✗ DISABLED'}
- **Job Title:** ${user.jobTitle ?? 'N/A'}
- **Department:** ${user.department ?? 'N/A'}`;

    // Risk info
    if (risk) {
      const riskIcon = getRiskIcon(risk.riskLevel);
      section += `\n\n**Identity Protection Risk:**
- **Risk Level:** ${riskIcon} ${risk.riskLevel?.toUpperCase() ?? 'Unknown'}
- **Risk State:** ${risk.riskState}`;
      if (risk.riskDetail && risk.riskDetail !== 'none') {
        section += `\n- **Risk Detail:** ${risk.riskDetail}`;
      }
    }

    // Recent sign-ins
    if (signIns.length > 0) {
      section += `\n\n**Recent Sign-ins (${signIns.length} total):**`;
      signIns.slice(0, 5).forEach(signIn => {
        const status = signIn.status.errorCode === 0 ? '✓' : `✗ (${signIn.status.errorCode})`;
        const location = formatSignInLocation(signIn);
        const riskFlag = signIn.riskLevelDuringSignIn && signIn.riskLevelDuringSignIn !== 'none' 
          ? ` [Risk: ${signIn.riskLevelDuringSignIn}]` 
          : '';
        section += `\n- ${status} **${signIn.appDisplayName}** from ${signIn.ipAddress} (${location})${riskFlag}`;
      });
    }

    // User's devices
    if (devices.length > 0) {
      section += `\n\n**Registered Devices (${devices.length}):**`;
      devices.slice(0, 3).forEach(d => {
        const compliance = d.isCompliant ? '✓ Compliant' : d.isManaged ? '○ Managed' : '✗ Unmanaged';
        section += `\n- ${d.displayName} (${d.operatingSystem}) - ${compliance}`;
      });
    }
  }

  return section;
}

function buildDeviceInvestigationsSection(
  devices: Map<string, DeviceInvestigationResult>
): string {
  let section = `## Investigated Devices (${devices.size})`;

  for (const [, result] of devices) {
    const { device } = result;
    const compliance = device.isCompliant 
      ? '✓ Compliant' 
      : device.isManaged 
        ? '○ Managed only' 
        : '✗ UNMANAGED';

    section += `\n\n### ${device.displayName}
- **Device ID:** ${device.deviceId}
- **OS:** ${device.operatingSystem} ${device.operatingSystemVersion}
- **Compliance:** ${compliance}
- **Trust Type:** ${device.trustType ?? 'N/A'}
- **Last Sign-in:** ${device.approximateLastSignInDateTime ? formatDateTime(device.approximateLastSignInDateTime) : 'Unknown'}`;

    if (device.manufacturer || device.model) {
      section += `\n- **Hardware:** ${[device.manufacturer, device.model].filter(Boolean).join(' ')}`;
    }
  }

  return section;
}

// ============================================================================
// HELPERS
// ============================================================================

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatSignInLocation(signIn: InvestigationSignIn): string {
  const parts = [
    signIn.location.city,
    signIn.location.state,
    signIn.location.countryOrRegion,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : 'Unknown location';
}

function groupEntitiesByType(entities: Entity[]): Record<string, Entity[]> {
  return entities.reduce((acc, entity) => {
    const type = entity.type;
    if (!acc[type]) acc[type] = [];
    acc[type].push(entity);
    return acc;
  }, {} as Record<string, Entity[]>);
}

function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function getRiskIcon(riskLevel: string | null | undefined): string {
  switch (riskLevel) {
    case 'high': return '🔴';
    case 'medium': return '🟠';
    case 'low': return '🟡';
    case 'none': return '🟢';
    default: return '⚪';
  }
}
